import { getJsonValueAtPath } from '@backstage/filter-predicates';

// `@backstage/filter-predicates` exports the `getJsonValueAtPath` function but
// not the `JsonValue` type it operates on, and this package does not otherwise
// depend on `@backstage/types`. Deriving the type from the function's own
// signature avoids adding a dependency purely for a type import. `Exclude`,
// not `NonNullable`: `JsonValue` itself includes `null` as a primitive, and
// `NonNullable` would strip that member too, not just the outer `undefined`.
export type JsonValue = Exclude<
  Parameters<typeof getJsonValueAtPath>[0],
  undefined
>;

/**
 * Narrows `unknown` (form data read from RJSF, which the type system cannot
 * prove is JSON) down to `JsonValue` so it can be passed to
 * `getJsonValueAtPath`. Every value that ever reaches one of the picker-shared
 * fields comes from RJSF form state, which is always JSON, so this only ever
 * rejects a value when something has gone wrong upstream — in which case
 * treating it as "no value" (via the caller's `undefined` fallback) is the
 * safe response.
 */
export function isJsonValue(value: unknown): value is JsonValue {
  if (value === null) return true;
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return true;
  }
  if (Array.isArray(value)) return value.every(isJsonValue);
  if (typeof value === 'object') return Object.values(value).every(isJsonValue);
  return false;
}

/** A JSON object, narrowed out of `JsonValue` (excludes `null` and arrays). */
export function isJsonObject(
  value: JsonValue,
): value is { [key: string]: JsonValue | undefined } {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
